import { useRef, type ReactNode } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useAuth } from "../../lib/useAuth";
import {
  flattenHistory,
  groupHistoryByDay,
  useWatchHistory,
  useWatchStateSync,
} from "../../lib/profile";
import { monthYear } from "../../lib/timeAgo";
import { useScrollFade } from "../../lib/useScrollFade";
import {
  HistoryEmptyState,
  HistoryListSkeleton,
  HistoryRow,
} from "../../components/profile/history-cards";
import { SheetHeader, SheetInset, SheetMessage } from "../../components/settings-sheet";
import { ErrorNote, Spinner } from "../../components/ui";
import { ArrowRightIcon } from "../../components/icons";

/**
 * The full watch history (the profile rail's See all): the settings'
 * frosted sheet over the same blurred still, rows grouped under Today /
 * Yesterday / weekday / date headers with the time of day on the right,
 * newest first, paged with Load more in the sheet's foot. The `profile_`
 * prefix un-nests this route from /profile (claim_.approve's trick), so
 * the profile page needs no <Outlet/>.
 */
export const Route = createFileRoute("/_authed/profile_/history")({
  component: WatchHistoryPage,
});

/** The sheet's gutter, on the rows so the hairlines run full width. */
const GUTTER = "px-[var(--rago-sheet-x)]";

function WatchHistoryPage() {
  const state = useAuth();
  const user = state.status === "signedIn" ? state.user : null;
  const sync = useWatchStateSync();
  const history = useWatchHistory();
  const shellRef = useRef<HTMLDivElement>(null);
  useScrollFade(shellRef);
  if (!user) return null;

  const syncKnown = sync.data !== undefined;
  const enabled = sync.data?.enabled ?? false;
  const entries = flattenHistory(history.data);
  const sinceLabel = user.createdAt ? monthYear(user.createdAt) : "";

  let body: ReactNode;
  if (history.isLoading || sync.isLoading) {
    body = <HistoryListSkeleton className={GUTTER} />;
  } else if (history.isError) {
    body = (
      <SheetInset>
        <ErrorNote error={history.error} />
      </SheetInset>
    );
  } else if (entries.length === 0) {
    body = <HistoryEmptyState syncOn={enabled || !syncKnown} minH="min-h-[40vh]" />;
  } else {
    body = (
      <>
        {groupHistoryByDay(entries).map((g) => (
          <section key={g.key} aria-label={g.label}>
            <h2 className={`rago-caps ${GUTTER} pt-6 pb-2`}>{g.label}</h2>
            <ul className="divide-y divide-[var(--rago-line-1)] border-t border-[var(--rago-line-1)]">
              {g.entries.map((e) => (
                <HistoryRow key={e.id} entry={e} className={GUTTER} />
              ))}
            </ul>
          </section>
        ))}
        <footer className="rago-sheet-foot flex items-center gap-3">
          {history.hasNextPage ? (
            <button
              type="button"
              className="rago-btn rago-btn-quiet rago-btn-sm"
              disabled={history.isFetchingNextPage}
              onClick={() => void history.fetchNextPage()}
            >
              {history.isFetchingNextPage ? <Spinner size={13} /> : null}
              Load more
            </button>
          ) : (
            <p className="text-xs text-[var(--rago-text-3)]">That&apos;s everything.</p>
          )}
        </footer>
      </>
    );
  }

  return (
    <div ref={shellRef} className="rago-sheet-shell rago-scroll-fade">
      <div className="rago-sheet">
        <SheetHeader
          title="Watch history"
          lede={
            sinceLabel
              ? `Everything you've watched on your servers since ${sinceLabel}, newest first.`
              : "Everything you've watched on your servers, newest first."
          }
          action={
            <Link to="/profile" className="rago-btn rago-btn-quiet rago-btn-sm select-none">
              <ArrowRightIcon size={14} className="rotate-180" />
              Profile
            </Link>
          }
        />
        {syncKnown && !enabled && entries.length > 0 ? (
          <SheetMessage>Sync is off — new plays aren&apos;t being recorded.</SheetMessage>
        ) : null}
        {body}
      </div>
    </div>
  );
}
